/**
 * Fix UTF-8 encoding issues where characters are incorrectly decoded
 * Converts mojibake (garbled text) back to proper UTF-8
 * Example: "SoirÃ©e" → "Soirée"
 */
export const fixUTF8Encoding = (text) => {
    if (!text || typeof text !== 'string') return text;

    try {
        const needsFix = /Ã©|Ã¨|Ã |Ã¢|Ã§|Ã´|Ã®|Ãª|Ã¯|Ã¼|Ã»|Ã¹/.test(text);

        if (!needsFix) return text;

        const bytes = new Uint8Array(text.split('').map(char => char.charCodeAt(0)));

        const decoder = new TextDecoder('utf-8');
        return decoder.decode(bytes);
    } catch (e) {
        console.error('Error fixing UTF-8 encoding:', e);
        return text;
    }
};

export const fixUTF8InObject = (obj) => {
    if (!obj) return obj;

    if (typeof obj === 'string') {
        return fixUTF8Encoding(obj);
    }

    if (Array.isArray(obj)) {
        return obj.map(item => fixUTF8InObject(item));
    }

    if (typeof obj === 'object') {
        const fixed = {};
        for (const key in obj) {
            if (obj.hasOwnProperty(key)) {
                fixed[key] = fixUTF8InObject(obj[key]);
            }
        }
        return fixed;
    }

    return obj;
};

/**
 * Normalize bullet-list text that was flattened from HTML <ul><li>…</li></ul>
 * during the EA → EoSv2 migration. Collapses aberrant whitespace, forces each
 * marker onto its own line, and indents sub-bullets following the Word
 * convention so nested lists read as a hierarchy.
 *
 * Markers handled (same class as validation in CommonInputs):
 *   -  • ◦ ▪ ▫ ▸ ➔ ★ ✔ ✗ ♦ ◊ ❏ ❖ ➢
 *
 * Hierarchy (8 spaces per level, Word-like — clearly visible on a
 * proportional font):
 *   level 0 : -           "- item"
 *   level 1 : •           "        • sub-item"
 *   level 2 : ◦           "                ◦ sub-sub-item"
 *   level 3 : ▪ ▫         "                        ▪ deeper"
 *   level 4 : ▸ ➔         "                                ▸ deeper still"
 *   decorative (★ ✔ ✗ ♦ ◊ ❏ ❖ ➢) : kept flush-left, treated as plain markers.
 */
const BULLET_LEVELS = {
    '•': 1,
    '◦': 2,
    '▪': 3,
    '▫': 3,
    '▸': 4,
    '➔': 4,
};

export const normalizeBulletList = (text) => {
    if (!text || typeof text !== 'string') return text;

    const BULLETS = '•◦▪▫▸➔★✔✗♦◊❏❖➢';
    const ALL_MARKERS = `-${BULLETS}`;

    const collapsed = text
        // 1. Normalize Windows line endings.
        .replace(/\r\n/g, '\n')
        // 2. Strip any leading whitespace before a marker — indentation will
        //    be re-applied based on the marker type in the final pass.
        .replace(new RegExp(`^[ \\t]+([${ALL_MARKERS}])`, 'gm'), '$1')
        // 3. Collapse multiple whitespaces right after any marker.
        .replace(new RegExp(`([${ALL_MARKERS}])[ \\t]{2,}`, 'g'), '$1 ')
        // 4. Force a line break before any Word-style bullet that appears
        //    mid-line — whether glued to text ("détaillé•") or separated by
        //    spaces ("intégration  •"). These markers never occur inside
        //    normal prose, so matching aggressively is safe.
        .replace(new RegExp(`([^\\n])[ \\t]*([${BULLETS}])`, 'g'), '$1\n$2')
        // 5. Force a line break before a hyphen only when it is surrounded by
        //    whitespace — spares hyphenated words like "après-midi".
        .replace(/([^\n])[ \t]+(-)(?=[ \t])/g, '$1\n$2');

    // 6. Drop "marker-only" lines (a bullet with no text after it) — they
    //    appear as flotsam when two markers were originally adjacent in the
    //    flattened HTML, e.g. "•        -" becoming "•\n-".
    // 7. Apply hierarchical indentation based on the leading marker of each
    //    line. Unknown markers and continuation lines stay flush-left.
    return collapsed
        .split('\n')
        .filter(
            (line) => !/^[ \t]*[-•◦▪▫▸➔★✔✗♦◊❏❖➢][ \t]*$/.test(line)
        )
        .map((line) => {
            const match = line.match(/^([-•◦▪▫▸➔])(?=\s|$)/);
            if (!match) return line;
            const level = BULLET_LEVELS[match[1]] ?? 0;
            return level > 0 ? '        '.repeat(level) + line : line;
        })
        .join('\n');
};
